/* ============================================================
   TPW · THE INDEX — collection refine controller
   Midas · 2026-06-30 — UNIFIED off-canvas pass.

   One STATIC top-toolbar "Filter" button + one left-sliding PANEL on
   EVERY device. This controller owns the whole interaction (no theme
   menu-drawer dependency for the panel UI):

   - OPEN / CLOSE the #TpwIndexPanel (slide from left) + a dimmed
     backdrop, driven by .tpw-rail-trigger;
   - scroll-lock the body while open;
   - focus-trap inside the panel; restore focus to the button on
     close; honour ESC;
   - click-outside / backdrop tap closes (fixes the mobile dismiss
     bug — the theme drawer had no body-click handler);
   - force every facet group [open] so all options are visible with
     no extra clicks;
   - keep the red-datum "N ACTIVE · CLEAR" line + the trigger
     badge + the grid-head active-filter CHIPS in sync after every
     filter (facets.js re-renders .active-facets-desktop + the
     product count; we observe both).

   Filtering itself stays the theme's facets.js (instant, no Apply).
   Everything is re-derived from the live DOM so it survives the
   facets.js innerHTML re-render after each filter.
   ============================================================ */
(function () {
  if (window.__tpwIndex) return;
  window.__tpwIndex = true;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  /* -------- references (re-queried lazily; some are re-rendered) -------- */
  function panel()    { return qs('#TpwIndexPanel'); }
  function backdrop() { return qs('[data-tpw-backdrop]'); }
  function trigger()  { return qs('.tpw-rail-trigger'); }

  /* count of active desktop facet chips = the true "armed" count.
     facets.js re-renders .active-facets-desktop on every filter. */
  function activeCount() {
    var wrap = qs('.active-facets-desktop .active-facets__wrapper');
    if (!wrap) return 0;
    return wrap.querySelectorAll('.active-facets__button').length;
  }

  /* ================= OPEN / CLOSE ================= */
  var lastFocus = null;

  function isOpen() {
    var p = panel();
    return !!(p && p.classList.contains('is-open'));
  }

  function openPanel() {
    var p = panel(); var b = backdrop(); var t = trigger();
    if (!p || isOpen()) return;
    lastFocus = document.activeElement;
    p.removeAttribute('inert'); // re-enable interaction + AT
    if (b) {
      b.hidden = false;
      // force a style flush so the un-hidden backdrop transitions from opacity 0
      void b.offsetWidth;
    }
    // open synchronously — do NOT defer to rAF (rAF can be starved on a heavy
    // page, leaving the panel un-opened). Transition still runs off the
    // compositor via translate3d + will-change.
    p.classList.add('is-open');
    if (b) b.classList.add('is-open');
    document.body.classList.add('tpw-index-open');
    if (t) t.setAttribute('aria-expanded', 'true');
    forceGroupsOpen();
    // move focus into the panel (close button first)
    var first = qs('[data-tpw-close]', p) || p;
    window.setTimeout(function () { try { first.focus(); } catch (e) {} }, REDUCED.matches ? 0 : 60);
    document.addEventListener('keydown', onKeydown, true);
  }

  function closePanel(restore) {
    var p = panel(); var b = backdrop(); var t = trigger();
    if (!p || !isOpen()) return;
    p.classList.remove('is-open');
    if (b) {
      b.classList.remove('is-open');
      // hide after the transition so it can't catch clicks
      var hide = function () { b.hidden = true; b.removeEventListener('transitionend', hide); };
      if (REDUCED.matches) hide();
      else { b.addEventListener('transitionend', hide); window.setTimeout(hide, 400); }
    }
    document.body.classList.remove('tpw-index-open');
    if (t) t.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', onKeydown, true);
    if (restore !== false) {
      var target = (lastFocus && document.contains(lastFocus)) ? lastFocus : t;
      if (target) { try { target.focus(); } catch (e) {} }
    }
    // after focus has left the panel, mark it inert (out of tab order + AT).
    // delayed so focus restoration isn't blocked by inert on a focused child.
    window.setTimeout(function () { if (!isOpen()) p.setAttribute('inert', ''); }, 0);
  }

  function onKeydown(e) {
    if (!isOpen()) return;
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      closePanel(true);
      return;
    }
    if (e.key === 'Tab') trapTab(e);
  }

  /* focus-trap: keep Tab inside the panel while open */
  function focusables() {
    var p = panel();
    if (!p) return [];
    return qsa(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      p
    ).filter(function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
    });
  }
  function trapTab(e) {
    var f = focusables();
    if (!f.length) { e.preventDefault(); return; }
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }

  /* force every facet disclosure open so all options show with no clicks */
  function forceGroupsOpen() {
    var p = panel();
    if (!p) return;
    qsa('details.facets__disclosure', p).forEach(function (d) {
      if (!d.open) d.open = true;
    });
  }

  /* ================= SYNC: datum / badge / chips ================= */
  function syncDatum() {
    var datum = qs('[data-tpw-datum]');
    if (!datum) return;
    var n = activeCount();
    var nEl = datum.querySelector('[data-tpw-datum-n]');
    if (nEl) nEl.textContent = n;
    datum.hidden = n === 0;
  }

  function syncTriggerBadge() {
    var t = trigger();
    if (!t) return;
    var n = activeCount();
    var badge = t.querySelector('[data-tpw-trigger-n]');
    if (badge) {
      badge.textContent = n;
      badge.hidden = n === 0;
    }
  }

  /* clone the live active-facet chips into the grid head (visible when the
     panel is closed). The clones are <facet-remove> elements; they re-upgrade
     on connect, and their remove link falls back to the first facet-filters-form,
     so removing a chip still filters instantly. */
  function syncGridChips() {
    var host = qs('[data-tpw-gridchips]');
    var src = qs('.active-facets-desktop .active-facets__wrapper');
    if (!host) return;
    if (!src || activeCount() === 0) { host.innerHTML = ''; return; }
    var clone = src.cloneNode(true);
    host.innerHTML = '';
    host.appendChild(clone);
  }

  /* ================= run after every state change ================= */
  function refresh() {
    syncDatum();
    syncTriggerBadge();
    syncGridChips();
    if (isOpen()) forceGroupsOpen();
  }

  /* ================= wiring ================= */
  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('.tpw-rail-trigger');
    if (t) { e.preventDefault(); isOpen() ? closePanel(true) : openPanel(); return; }

    if (e.target.closest && e.target.closest('[data-tpw-close]')) {
      e.preventDefault(); closePanel(true); return;
    }
    // backdrop / outside tap → close (fixes the mobile dismiss bug)
    if (e.target.closest && e.target.closest('[data-tpw-backdrop]')) {
      closePanel(true); return;
    }
    // keep facet groups permanently open: swallow native <summary> toggling
    // inside the panel (the group label is not a collapse control here).
    var sum = e.target.closest && e.target.closest('#TpwIndexPanel .facets__disclosure > .facets__summary');
    if (sum) { e.preventDefault(); return; }

    // a CLEAR / chip-remove link navigates the page; nothing to do here —
    // the count re-renders and refresh() runs via the observer.
  });

  function debounceLite(fn, wait) {
    var to;
    return function () { clearTimeout(to); to = setTimeout(fn, wait); };
  }

  window.addEventListener('resize', debounceLite(function () { refresh(); }, 120));
  window.addEventListener('popstate', function () { window.requestAnimationFrame(refresh); });

  /* facets.js replaces .active-facets-desktop + #ProductCount innerHTML on
     every filter; observe both so the datum / badge / chips re-sync without a
     reload. */
  function observe() {
    if (!window.MutationObserver) return;
    var active = qs('.active-facets-desktop');
    if (active) {
      new MutationObserver(function () { window.requestAnimationFrame(refresh); })
        .observe(active, { childList: true, subtree: true });
    }
    var count = document.getElementById('ProductCount');
    if (count) {
      new MutationObserver(function () { window.requestAnimationFrame(refresh); })
        .observe(count, { childList: true, characterData: true, subtree: true });
    }
  }

  /* the re-hosted .facets--desktop wrapper carries its own role=dialog/aria-modal
     (theme horizontal markup). Our .tpw-rail IS the dialog now — strip the nested
     dialog semantics so AT sees a single dialog. */
  function dedupeDialog() {
    var inner = qs('#TpwIndexPanel #FacetsWrapperDesktop');
    if (inner) {
      inner.removeAttribute('role');
      inner.removeAttribute('aria-modal');
      inner.removeAttribute('tabindex');
    }
  }

  function init() {
    dedupeDialog();
    // panel starts closed → inert (keeps its focusables out of the tab order
    // without a visibility transition that stalled the slide)
    var p0 = panel();
    if (p0 && !p0.classList.contains('is-open')) p0.setAttribute('inert', '');
    forceGroupsOpen();
    observe();
    refresh();
    window.tpwIndexRefresh = refresh;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
